import React, { useEffect, useRef } from 'react';
import { Heart, Star, Check, Shield, Gift, BookOpen, Sparkles, Lock, Zap, Quote, Crown, Gem } from 'lucide-react';
import { Button } from './ui/Button';
import { CountdownTimer } from './ui/CountdownTimer';
export function LandingPage() {
  const scrollToSales = () => {
    const element = document.getElementById('sales-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const ebookImage = 'https://cdn.magicpatterns.com/uploads/tvespSAXLpYAA52p6yYCQf/Untitled_design-5.png';
  // Scroll reveal effect
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FAF7F5]" data-id="element-1">
      {/* Floating Particles Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" data-id="element-2">
        <div className="absolute top-20 left-10 w-2 h-2 bg-[#C9A86A]/30 rounded-full animate-float" style={{
        animationDelay: '0s'
      }} data-id="element-3" />
        <div className="absolute top-40 right-20 w-3 h-3 bg-[#D4A5A5]/20 rounded-full animate-float" style={{
        animationDelay: '1s'
      }} data-id="element-4" />
        <div className="absolute top-60 left-1/4 w-2 h-2 bg-[#C9A86A]/20 rounded-full animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-5" />
        <div className="absolute bottom-40 right-1/3 w-2 h-2 bg-[#E8D5D0]/40 rounded-full animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-6" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#C9A86A]/10" data-id="element-7">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-8">
          <div className="text-xl font-serif font-bold tracking-[0.2em] text-[#3A3A3A]" data-id="element-9">
            AMOR<span className="text-gradient-gold" data-id="element-10">CONSCIENTE</span>
          </div>
          <Button onClick={scrollToSales} variant="outline" className="text-sm px-6 py-2" data-id="element-11">
            OBTENER AHORA
          </Button>
        </div>
      </nav>

      {/* 1. HERO SECTION */}
      <header className="relative min-h-screen flex items-center justify-center px-6 pt-20 overflow-hidden" data-id="element-12">
        {/* Animated Background */}
        <div className="absolute inset-0 -z-10" data-id="element-13">
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-[#FAF7F5] to-[#FAF7F5]" data-id="element-14" />
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-[#C9A86A]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '4s'
        }} data-id="element-15" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D4A5A5]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '5s'
        }} data-id="element-16" />
        </div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center" data-id="element-17">
          {/* Left: Text Content */}
          <div className="text-center lg:text-left space-y-8 animate-fade-in-up" data-id="element-18">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass border border-[#C9A86A]/30 text-sm font-medium text-[#C9A86A]" data-id="element-19">
              <Crown className="w-4 h-4" data-id="element-20" />
              <span className="tracking-wide" data-id="element-21">Para mujeres de alto valor</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-[#3A3A3A] leading-[1.1]" data-id="element-22">
              Manifiesta a Tu{' '}
              <span className="block italic text-gradient-gold mt-2" data-id="element-23">
                Pareja Ideal
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed" data-id="element-24">
              Reprograma tu mente, sana patrones emocionales y atrae una
              relación consciente y extraordinaria en 90 días.
            </p>

            <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-gray-500" data-id="element-25">
              {['Método probado', 'Ejercicios guiados', 'Acceso instantáneo'].map((item, i) => <span key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-[#E8D5D0]/50" data-id="element-26">
                  <Check className="w-4 h-4 text-[#C9A86A]" data-id="element-27" />
                  {item}
                </span>)}
            </div>

            <div className="pt-4" data-id="element-28">
              <Button onClick={scrollToSales} variant="luxury" withSparkle className="text-lg px-10 py-5" data-id="element-29">
                QUIERO TRANSFORMAR MI VIDA
              </Button>
            </div>
          </div>

          {/* Right: Floating Ebook */}
          <div className="relative flex justify-center animate-scale-in" style={{
          animationDelay: '0.3s'
        }} data-id="element-30">
            <div className="relative animate-float-subtle" data-id="element-31">
              <img src={ebookImage} alt="Manifiesta tu Pareja Ideal" className="w-full max-w-[400px] h-auto drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)]" data-id="element-32" />
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#C9A86A]/20 to-transparent rounded-3xl blur-3xl -z-10 scale-110" data-id="element-33" />
              {/* Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse" data-id="element-34">
                ⭐ BESTSELLER
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#C9A86A]/60 animate-bounce" data-id="element-35">
          <span className="text-xs tracking-widest" data-id="element-36">DESCUBRE MÁS</span>
          <div className="w-6 h-10 rounded-full border-2 border-current flex justify-center pt-2" data-id="element-37">
            <div className="w-1 h-2 bg-current rounded-full animate-pulse" data-id="element-38" />
          </div>
        </div>
      </header>

      {/* 2. VALUE PROPOSITION */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#FAF7F5] to-white" data-id="element-39">
        <div className="max-w-6xl mx-auto" data-id="element-40">
          <div className="reveal grid md:grid-cols-2 gap-8 items-center" data-id="element-41">
            <div className="space-y-6 bg-white/90 backdrop-blur rounded-[2rem] p-8 md:p-10 shadow-xl border border-[#C9A86A]/10 card-luxury" data-id="element-42">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#C9A86A]/10 to-[#D4A5A5]/10 border border-[#C9A86A]/20 text-xs font-bold text-[#C9A86A] uppercase tracking-wider" data-id="element-43">
                <Gem className="w-3 h-3" data-id="element-44" />
                Transformación Premium
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] leading-tight" data-id="element-45">
                Un sistema exclusivo para mujeres que saben que merecen más.
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-46">
                Esta no es otra guía de citas. Es una inmersión profunda en tu
                programación subconsciente, diseñada para desmantelar las
                barreras que te han impedido recibir el amor extraordinario que
                mereces.
              </p>
              <div className="flex items-center gap-4 pt-4" data-id="element-47">
                <div className="flex -space-x-3" data-id="element-48">
                  {[1, 2, 3, 4, 5].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-[#E8D5D0] to-[#D4A5A5] shadow-md" data-id="element-49" />)}
                </div>
                <div data-id="element-50">
                  <div className="flex gap-0.5 text-[#C9A86A]" data-id="element-51">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-52" />)}
                  </div>
                  <span className="text-sm text-gray-500" data-id="element-53">
                    +2,000 mujeres transformadas
                  </span>
                </div>
              </div>
            </div>

            <div className="relative flex justify-center" data-id="element-54">
              <div className="animate-float-subtle" style={{
              animationDelay: '0.5s'
            }} data-id="element-55">
                <img src={ebookImage} alt="Ebook" className="w-full max-w-[320px] drop-shadow-[0_40px_80px_rgba(201,168,106,0.35)]" data-id="element-56" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PAIN POINTS */}
      <section className="py-16 px-6 bg-white" data-id="element-57">
        <div className="max-w-4xl mx-auto" data-id="element-58">
          <div className="text-center mb-12 reveal" data-id="element-59">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-60">
              ¿Te identificas?
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-3" data-id="element-61">
              Este libro es para ti si...
            </h2>
          </div>

          <div className="space-y-4" data-id="element-62">
            {['Te apegas emocionalmente demasiado rápido y terminas decepcionada', 'Atraes repetidamente parejas emocionalmente no disponibles', 'Experimentas ansiedad, apego o miedo a estar sola', 'Repites los mismos patrones de relación con diferentes personas', 'Conoces tu valor, pero tu vida amorosa no lo refleja'].map((item, index) => <div key={index} className="reveal flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#FAF7F5] to-white border border-[#E8D5D0]/50 shadow-sm hover:shadow-lg hover:border-[#C9A86A]/30 transition-all duration-500 card-luxury" style={{
            animationDelay: `${index * 100}ms`
          }} data-id="element-63">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A86A] to-[#D4A5A5] flex items-center justify-center shadow-md" data-id="element-64">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-65" />
                </div>
                <p className="text-gray-700 font-medium" data-id="element-66">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. WHAT'S INSIDE */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-[#FAF7F5]" data-id="element-67">
        <div className="max-w-6xl mx-auto" data-id="element-68">
          <div className="text-center mb-12 reveal" data-id="element-69">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-70">
              Contenido Exclusivo
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-3" data-id="element-71">
              Qué Obtendrás Dentro
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-72">
            {[{
            icon: Heart,
            title: 'Sana Patrones Emocionales',
            desc: 'Identifica y sana los bloqueos que mantienen el amor alejado.'
          }, {
            icon: Sparkles,
            title: 'Reprogramación Subconsciente',
            desc: 'Reconfigura tu mente para atraer amor consciente.'
          }, {
            icon: Lock,
            title: 'Libera el Apego',
            desc: 'Aprende a soltar sin cerrar tu corazón.'
          }, {
            icon: Star,
            title: 'Fortalece tu Autoestima',
            desc: 'Construye confianza inquebrantable.'
          }, {
            icon: BookOpen,
            title: 'Manifestación Práctica',
            desc: 'Activa un proceso real de atracción.'
          }, {
            icon: Shield,
            title: 'Límites Saludables',
            desc: 'Define estándares en las relaciones.'
          }].map((item, index) => <div key={index} className="reveal group relative bg-white p-8 rounded-2xl shadow-lg border border-[#C9A86A]/10 overflow-hidden card-luxury" style={{
            animationDelay: `${index * 100}ms`
          }} data-id="element-73">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C9A86A] via-[#D4A5A5] to-[#C9A86A] opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-74" />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FAF7F5] to-[#E8D5D0] flex items-center justify-center mb-6 text-[#C9A86A] group-hover:scale-110 transition-transform duration-500" data-id="element-75">
                  <item.icon className="w-6 h-6" strokeWidth={1.5} data-id="element-76" />
                </div>
                <h3 className="text-xl font-serif font-bold text-[#3A3A3A] mb-3" data-id="element-77">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-78">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. BENEFITS - Dark Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#3A3A3A] via-[#2A2A2A] to-[#3A3A3A] text-white relative overflow-hidden" data-id="element-79">
        <div className="absolute inset-0 opacity-10" data-id="element-80">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-81" />
        </div>
        <div className="absolute top-0 left-1/4 w-80 h-80 bg-[#C9A86A] rounded-full blur-[150px] opacity-10" data-id="element-82" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#D4A5A5] rounded-full blur-[150px] opacity-10" data-id="element-83" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-84">
          <div className="text-center mb-12 reveal" data-id="element-85">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-86">
              Tu Transformación
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#E8D5D0] mt-3" data-id="element-87">
              Lo que lograrás
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-88">
            {[{
            title: 'Claridad Emocional',
            desc: 'Recupera tu poder interior y deja de sentirte confundida.',
            icon: Zap
          }, {
            title: 'Atracción Natural',
            desc: 'Deja de perseguir y comienza a atraer parejas alineadas.',
            icon: Heart
          }, {
            title: 'Alineación Saludable',
            desc: 'Relaciones estables y recíprocas que se sienten seguras.',
            icon: Shield
          }].map((benefit, index) => <div key={index} className="reveal relative glass-dark border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all duration-500 card-luxury" data-id="element-89">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C9A86A] to-transparent opacity-50" data-id="element-90" />
                <div className="flex items-center gap-3 mb-4" data-id="element-91">
                  <div className="w-10 h-10 rounded-lg bg-[#C9A86A]/20 flex items-center justify-center" data-id="element-92">
                    <benefit.icon className="w-5 h-5 text-[#C9A86A]" data-id="element-93" />
                  </div>
                  <span className="text-4xl font-serif text-[#C9A86A]/30" data-id="element-94">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="text-xl font-serif font-bold mb-3 text-[#FAF7F5]" data-id="element-95">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-96">
                  {benefit.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. TESTIMONIALS */}
      <section className="py-16 px-6 bg-[#FAF7F5]" data-id="element-97">
        <div className="max-w-6xl mx-auto" data-id="element-98">
          <div className="text-center mb-12 reveal" data-id="element-99">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-100">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-3" data-id="element-101">
              Lo que dicen nuestras lectoras
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-102">
            {[{
            quote: 'Este ebook me abrió los ojos. Entendí por qué siempre atraía el mismo tipo de hombre. Ahora estoy en una relación sana.',
            name: 'Carolina M.',
            location: 'Ciudad de México',
            highlight: 'Relación sana'
          }, {
            quote: 'Lloré leyendo los primeros capítulos porque me sentí identificada. Los ejercicios cambiaron mi forma de verme.',
            name: 'Valentina R.',
            location: 'Buenos Aires',
            highlight: 'Transformación'
          }, {
            quote: 'Después de 3 relaciones tóxicas, este libro me mostró que no estoy rota, solo necesitaba sanar.',
            name: 'Mariana L.',
            location: 'Bogotá',
            highlight: 'Sanación'
          }, {
            quote: 'Es práctico. No es solo teoría, son ejercicios que puedes aplicar. En 2 meses vi cambios reales.',
            name: 'Sofía G.',
            location: 'Lima',
            highlight: '2 meses'
          }, {
            quote: 'Mi terapeuta me lo recomendó. Explica la neurociencia del apego de forma clara y aplicable.',
            name: 'Andrea P.',
            location: 'Santiago',
            highlight: 'Recomendado'
          }, {
            quote: 'Tenía miedo de estar sola. Ahora disfruto mi soledad y sé que el amor correcto llegará.',
            name: 'Daniela F.',
            location: 'Medellín',
            highlight: 'Amor propio'
          }].map((testimonial, index) => <div key={index} className="reveal relative bg-white p-6 rounded-2xl border border-[#E8D5D0]/50 shadow-sm hover:shadow-lg transition-all duration-500 card-luxury" data-id="element-103">
                <div className="absolute -top-3 left-6 w-6 h-6 bg-[#C9A86A] rounded-full flex items-center justify-center shadow-md" data-id="element-104">
                  <Quote className="w-3 h-3 text-white" data-id="element-105" />
                </div>
                <span className="inline-block px-3 py-1 bg-[#C9A86A]/10 text-[#C9A86A] text-xs font-semibold rounded-full mb-3" data-id="element-106">
                  {testimonial.highlight}
                </span>
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic" data-id="element-107">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3" data-id="element-108">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A86A] to-[#D4A5A5] flex items-center justify-center text-white font-serif font-bold text-xs" data-id="element-109">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div data-id="element-110">
                    <p className="font-semibold text-[#3A3A3A] text-sm" data-id="element-111">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-112">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Real Conversations */}
          <div className="reveal" data-id="element-113">
            <div className="text-center mb-8" data-id="element-114">
              <span className="text-[#D4A5A5] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-115">
                Conversaciones Reales
              </span>
              <h3 className="text-2xl md:text-3xl font-serif text-[#3A3A3A] mt-2" data-id="element-116">
                Así se sienten muchas antes de este camino
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-117">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-[#E8D5D0]/50 card-luxury" data-id="element-118">
                <img src="https://cdn.magicpatterns.com/uploads/isjLW8qSt5bb8Bx8kJDFMZ/Screenshot_2026-02-04_at_2.54.56_PM.png" alt="Conversación real" className="w-full h-auto" data-id="element-119" />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl border border-[#E8D5D0]/50 card-luxury" data-id="element-120">
                <img src="https://cdn.magicpatterns.com/uploads/dNqQJrmoHxGZXhxbjsjsBc/PHOTO-2026-02-04-14-39-39.jpg" alt="Conversación real" className="w-full h-auto" data-id="element-121" />
              </div>
            </div>
            <div className="text-center mt-8" data-id="element-122">
              <p className="text-gray-600 mb-6" data-id="element-123">
                <span className="font-semibold text-[#3A3A3A]" data-id="element-124">
                  No tienes que seguir sintiéndote así.
                </span>
              </p>
              <Button onClick={scrollToSales} variant="primary" data-id="element-125">
                QUIERO COMENZAR MI TRANSFORMACIÓN
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 7. SALES SECTION */}
      <section id="sales-section" className="py-16 px-6 bg-gradient-to-b from-white to-[#FAF7F5]" data-id="element-126">
        <div className="max-w-6xl mx-auto" data-id="element-127">
          <div className="text-center mb-10 reveal" data-id="element-128">
            <div className="inline-block px-6 py-2 rounded-full bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white text-sm font-bold uppercase tracking-widest shadow-lg animate-pulse" data-id="element-129">
              ✨ Oferta Especial por Tiempo Limitado
            </div>
            <h2 className="text-3xl md:text-5xl font-serif text-[#3A3A3A] mt-6 leading-tight" data-id="element-130">
              Todo lo que necesitas para atraer una{' '}
              <span className="italic text-gradient-gold" data-id="element-131">
                relación consciente
              </span>
            </h2>
          </div>

          <div className="reveal grid lg:grid-cols-2 gap-10 items-center" data-id="element-132">
            {/* Floating Ebook */}
            <div className="flex flex-col items-center space-y-6" data-id="element-133">
              <div className="relative animate-float-subtle" data-id="element-134">
                <img src={ebookImage} alt="Ebook" className="w-full max-w-[380px] drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)]" data-id="element-135" />
                <div className="absolute top-2 left-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase text-[#3A3A3A] shadow-lg border border-[#C9A86A]/20" data-id="element-136">
                  ⭐ Más Vendido
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 w-full max-w-[340px]" data-id="element-137">
                {[{
                label: 'Garantía',
                value: '7 días'
              }, {
                label: 'Acceso',
                value: 'Inmediato'
              }, {
                label: 'Formato',
                value: 'Digital'
              }].map((item, i) => <div key={i} className="text-center p-3 rounded-xl glass border border-[#E8D5D0]/50" data-id="element-138">
                    <div className="text-lg font-serif font-bold text-[#C9A86A]" data-id="element-139">
                      {item.value}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-140">
                      {item.label}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Sales Copy */}
            <div className="space-y-6 bg-white rounded-[2rem] p-8 shadow-2xl border border-[#C9A86A]/10" data-id="element-141">
              <div className="bg-gradient-to-br from-[#FAF7F5] to-white p-6 rounded-2xl border-2 border-[#C9A86A]/30 shadow-xl" data-id="element-142">
                <p className="text-gray-600 mb-4" data-id="element-143">
                  Un método probado para transformar tu vida amorosa para
                  siempre.
                </p>
                <div className="flex items-end gap-4" data-id="element-144">
                  <span className="text-6xl font-serif font-bold text-gradient-gold" data-id="element-145">
                    $27
                  </span>
                  <div className="flex flex-col pb-1" data-id="element-146">
                    <span className="text-xl text-gray-400 line-through" data-id="element-147">
                      $97
                    </span>
                    <span className="bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md" data-id="element-148">
                      72% OFF
                    </span>
                  </div>
                </div>
              </div>

              <CountdownTimer data-id="element-149" />

              <Button fullWidth variant="luxury" withSparkle className="text-xl py-6" data-id="element-150">
                SÍ, QUIERO EL EBOOK
              </Button>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-500" data-id="element-151">
                <span className="flex items-center gap-2" data-id="element-152">
                  <div className="w-2 h-2 rounded-full bg-[#C9A86A]" data-id="element-153" />
                  Acceso Instantáneo
                </span>
                <span className="flex items-center gap-2" data-id="element-154">
                  <div className="w-2 h-2 rounded-full bg-[#C9A86A]" data-id="element-155" />
                  Producto Digital
                </span>
              </div>

              <div className="bg-[#FAF7F5] rounded-xl p-5 border border-[#E8D5D0] flex gap-4 items-start" data-id="element-156">
                <div className="w-10 h-10 rounded-lg bg-[#C9A86A]/10 flex items-center justify-center flex-shrink-0" data-id="element-157">
                  <Shield className="w-5 h-5 text-[#C9A86A]" data-id="element-158" />
                </div>
                <div data-id="element-159">
                  <h4 className="font-bold text-[#3A3A3A] text-sm" data-id="element-160">
                    Garantía de 7 Días Sin Riesgo
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-161">
                    Si no resuena contigo, te reembolsaremos el 100%. Sin
                    preguntas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. BONUSES */}
      <section className="py-16 px-6 bg-white" data-id="element-162">
        <div className="max-w-6xl mx-auto" data-id="element-163">
          <div className="text-center mb-10 reveal" data-id="element-164">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase" data-id="element-165">
              Valor Agregado
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-3" data-id="element-166">
              Bonos Exclusivos Incluidos
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-167">
            {[{
            title: 'Audio Guiado',
            subtitle: 'Reprogramación Subconsciente',
            desc: 'Reconfigura tu mente mientras duermes.',
            value: '$47'
          }, {
            title: 'Cuaderno de Trabajo',
            subtitle: 'Desapego y Claridad',
            desc: 'Ejercicios para limpiar el desorden emocional.',
            value: '$37'
          }, {
            title: 'Ritual de 7 Días',
            subtitle: 'Sistema de Intención',
            desc: 'Práctica diaria para magnetizar tus deseos.',
            value: '$27'
          }].map((bonus, index) => <div key={index} className="reveal relative border-2 border-[#E8D5D0] rounded-2xl p-8 hover:border-[#C9A86A] transition-all duration-500 bg-gradient-to-br from-white to-[#FAF7F5] shadow-lg card-luxury" data-id="element-168">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C9A86A] via-[#D4A5A5] to-[#C9A86A] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-169" />
                <div className="flex items-center justify-between mb-4" data-id="element-170">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FAF7F5] to-[#E8D5D0] rounded-xl flex items-center justify-center text-[#C9A86A] shadow-sm" data-id="element-171">
                    <Gift className="w-6 h-6" data-id="element-172" />
                  </div>
                  <span className="text-xl font-serif font-bold text-[#C9A86A]" data-id="element-173">
                    {bonus.value}
                  </span>
                </div>
                <div className="text-xs font-bold text-[#C9A86A] mb-2 uppercase tracking-wider" data-id="element-174">
                  Bono {index + 1}
                </div>
                <h3 className="text-xl font-serif font-bold text-[#3A3A3A] mb-1" data-id="element-175">
                  {bonus.title}
                </h3>
                <h4 className="text-sm text-gray-500 mb-3" data-id="element-176">{bonus.subtitle}</h4>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-177">
                  {bonus.desc}
                </p>
              </div>)}
          </div>

          <div className="text-center mt-8 p-5 rounded-2xl bg-[#FAF7F5] border border-[#C9A86A]/20 reveal" data-id="element-178">
            <p className="text-lg font-semibold text-[#3A3A3A]" data-id="element-179">
              Valor Total:{' '}
              <span className="text-2xl text-gradient-gold font-serif" data-id="element-180">
                $111
              </span>{' '}
              <span className="text-gray-500" data-id="element-181">— Incluidos GRATIS</span>
            </p>
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#FAF7F5] to-white text-center relative overflow-hidden" data-id="element-182">
        <div className="absolute inset-0 opacity-30" data-id="element-183">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#C9A86A] rounded-full blur-[150px]" data-id="element-184" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 reveal" data-id="element-185">
          <div className="relative w-full max-w-[280px] mx-auto mb-10 animate-float-subtle" data-id="element-186">
            <img src={ebookImage} alt="Ebook" className="w-full drop-shadow-[0_40px_80px_rgba(201,168,106,0.4)]" data-id="element-187" />
          </div>

          <h2 className="text-4xl md:text-5xl font-serif text-[#3A3A3A] leading-tight mb-6" data-id="element-188">
            No estás rota.
            <br data-id="element-189" />
            Estás lista para{' '}
            <span className="italic text-gradient-gold" data-id="element-190">elegir diferente</span>.
          </h2>

          <p className="text-lg text-gray-600 max-w-xl mx-auto mb-8" data-id="element-191">
            Comienza tu viaje hacia el amor consciente hoy.
          </p>

          <Button onClick={scrollToSales} variant="luxury" withSparkle className="text-xl px-12 py-6" data-id="element-192">
            OBTENER EL EBOOK AHORA
          </Button>

          <div className="pt-12 mt-12 border-t border-[#E8D5D0]" data-id="element-193">
            <p className="text-sm text-gray-400" data-id="element-194">
              © 2024 Amor Consciente. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </section>
    </div>;
}